import { Chip, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { BudgetTableRow } from "../types";
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams, useGridApiRef } from "@mui/x-data-grid-pro";
import { useMoneyFormatter } from "@utils/numberUtils";
import { PlaylistAdd, AccountBalance, PaidOutlined, Receipt } from "@mui/icons-material";
import { useBudgetTableContext } from "../context/useBudgetTableContext";
import { ExpenseActions } from "../../Expenses/ExpenseActions";
import { useCostItemStatusOptions } from "./useCostItemStatusOptions";
import { BudgetItemActions } from "../../BudgetItemActions";
import { CostItemActions } from "../../CostItemActions";
import { BudgetItemFragmentFragment } from "gql/index";
import { useMemo, type ChangeEvent } from "react";
import { CostItemStatusChip } from "../../CostItemStatusChip";
import { ExpenseStatusChip } from "../../CommercialConstructionCalculator/components/ExpenseStatusChip";
import { QuoteActions } from "../../Quotes/QuoteActions";
import { useCurrentProject } from "@modules/projects/utils/useCurrentProject";

interface Props {
  isProjectArchived: boolean;
  budgetItems: BudgetItemFragmentFragment[];
  apiRef: ReturnType<typeof useGridApiRef>;
  onAddCostItem: (budgetId?: number) => void;
}
export const useBudgetTableColumns = ({ isProjectArchived, budgetItems, apiRef, onAddCostItem }: Props) => {
  const { formatMessage } = useIntl();
  const formatMoney = useMoneyFormatter();
  const statusOptions = useCostItemStatusOptions();
  const { canManageBudgetAndExpenses, isTemplate } = useCurrentProject();
  const budgetItemMap = useMemo(() => {
    return budgetItems.reduce<Record<number, BudgetItemFragmentFragment>>((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [budgetItems]);

  const { setEntryDialog } = useBudgetTableContext();

  const renderNameEditCell = (params: GridRenderEditCellParams<BudgetTableRow>) => {
    const placeholder = params.row.type === 'budget'
      ? formatMessage({ id: 'budget item name' })
      : params.row.type === 'cost'
        ? formatMessage({ id: 'cost item name' })
        : undefined;

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      params.api.setEditCellValue({ id: params.id, field: params.field, value: event.target.value });
    };

    return (
      <TextField
        fullWidth
        variant='standard'
        value={params.value ?? ''}
        placeholder={placeholder}
        onChange={handleChange}
        autoFocus={params.hasFocus}
      />
    );
  };
  let columns: GridColDef<BudgetTableRow>[] = [

    { field: 'name', headerName: 'Name', flex: 1, minWidth: 300, editable: true, renderEditCell: renderNameEditCell,
      renderCell: (params) => {
        const isEntryRow = params.row.type === 'expense' || params.row.type === 'quote';
        return (
          <Stack flex={1} direction='row' alignItems='center' gap={1} height='100%'
            paddingLeft={((params.row?.treePath?.length ?? 0) - 1) * 2}>
            {params.row.type === 'cost' && <PaidOutlined fontSize='small' />}
            {params.row.type === 'budget' && <AccountBalance fontSize='small' />}
            <Stack direction='column' gap={0.25} minWidth={0}>
              <Tooltip title={params.formattedValue}>
                <Typography fontWeight={600}>
                  {params.formattedValue}
                </Typography>
              </Tooltip>
              {isEntryRow && (
                <Chip
                  size='small'
                  label={formatMessage({ id: params.row.type === 'expense' ? 'Expense' : 'Quote' })}
                  variant='outlined'
                  sx={{ alignSelf: 'flex-start', fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Stack>
          </Stack>
        );
      }
     },
    {
      field: 'planned',
      headerName: formatMessage({ id: 'Planned' }),
      valueGetter: (_, row) => row.planned,
      valueFormatter: (value) => formatMoney(Number(value)),
      minWidth: 140,
      type: 'number',
      renderCell: (params) => (
        <Stack flex={1} height='100%' justifyContent='center'>
          <Typography>
            {params.formattedValue}
          </Typography>
        </Stack>
      ),
      editable: true
    },
    {
      field: 'quotedAmount',
      headerName: formatMessage({ id: 'Quoted' }),
      minWidth: 140,
      type: 'number',
      valueGetter: (_, row) => row.quotedAmount ?? 0,
      valueFormatter: (value) => isNaN(value as number) ? '-' : formatMoney(Number(value)),
      renderCell: (params) => (
        <Stack flex={1} height='100%' justifyContent='center'>
          <Typography>
            {params.formattedValue}
          </Typography>
        </Stack>
      ),
      editable: false
    },
    {
      field: 'toPayAmount',
      headerName: formatMessage({ id: 'To pay' }),
      minWidth: 140,
      type: 'number',
      valueGetter: (_, row) => row.toPayAmount ?? 0,
      valueFormatter: (value) => isNaN(value as number) ? '-' : formatMoney(Number(value)),
      renderCell: (params) => (
        <Stack flex={1} height='100%' justifyContent='center'>
          <Typography>
            {params.formattedValue}
          </Typography>
        </Stack>
      ),
      editable: false
    },
    {
      field: 'spent',
      headerName: formatMessage({ id: 'Spent' }),
      editable: false,
      minWidth: 140,
      type: 'number',
      valueGetter: (_, row) => row.spent,
      valueFormatter: (value) => isNaN(value as number) ? '' : formatMoney(Number(value)),
      renderCell: (params) => (
        <Stack flex={1} height='100%' justifyContent='center'>
          <Typography>
            {params.formattedValue}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'overrun',
      headerName: formatMessage({ id: 'Overrun' }),
      minWidth: 140,
      editable: false,
      type: 'number',
      valueGetter: (_, row) => {
        if (row.type === 'budget' || row.type === 'cost') {
          return row.planned ? Math.max(row.effectiveAmount - row.planned, 0) : 0;
        }
        else if (row.type === 'quote') {
          return row.effectiveAmount > (row.quotedAmount ?? 0) ? row.effectiveAmount - (row.quotedAmount ?? 0) : 0;
        }
        return 0;
      },
      valueFormatter: (value) => isNaN(value as number) ? '' : formatMoney(Number(value)),
      renderCell: ({ formattedValue, value }) => {
        const isOverBudget = (value as number) > 0;
        return (
          <Stack flex={1} height='100%' justifyContent='center'>
            <Typography color={isOverBudget ? 'error.main' : 'success.main'}>
              {formattedValue}
            </Typography>
          </Stack>
        );
      }
    },
    {
      field: 'status',
      headerName: formatMessage({ id: 'Status' }),
    
      width: 150,
      editable: true,
      valueGetter: (_, row) => ('status' in row && row.status != null ? row.status : ''),
      valueOptions: statusOptions,
      type: 'singleSelect',
      valueFormatter: (value) => {
        const option = statusOptions.find(o => o.value === value);
        return option?.label ?? '-';
      },
      renderCell: ({ value, row }) => {
        if (row.type === 'expense' && row.status) {
          return (
            <Stack flex={1} height='100%' justifyContent='center' alignItems='center'>
              <ExpenseStatusChip status={row.status} size='small' />
            </Stack>
          );
        }

        if (row.type === 'quote') {
          return (
            <Stack flex={1} height='100%' justifyContent='center' alignItems='center'>
              <Chip color='default' label={formatMessage({ id: 'Quote' })} size='small' variant='outlined' />
            </Stack>
          );
        }

        const option = statusOptions.find(o => o.value === value);
        return (
          <Stack flex={1} height='100%' justifyContent='center' alignItems='center'>
            {value && option ? (
              <CostItemStatusChip status={option.value} size='small' />
            ) : (
              <Typography>
                {option?.label ?? ''}
              </Typography>
            )}
          </Stack>
        );
      },
    }];
 
    if (canManageBudgetAndExpenses) {
    columns.push({
      field: 'action',
      renderHeader: () => null,
      width: 140,
      editable: false,
      renderCell: isProjectArchived ? undefined : (params: GridRenderCellParams<BudgetTableRow>) => {
        const { row } = params;
        if (!row.id || row.id.startsWith('draft-')) return null;
        if (row.type === 'budget') {
          return (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='right'
              gap={1}
              height='100%' 
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title={formatMessage({ id: 'Add cost item' })}>
                <IconButton size='small' onClick={(e) => {
                  e.stopPropagation();
                  onAddCostItem(row.originalId);
                }}>
                  <PlaylistAdd />
                </IconButton>
              </Tooltip>
              {budgetItemMap[row.originalId] && (
                <BudgetItemActions
                  budgetItem={budgetItemMap[row.originalId]}
                  onEdit={() => apiRef.current?.startRowEditMode({ id: row.id, fieldToFocus: 'name' })}
                />
              )}
            </Stack>
          );
        }

        if (row.type === 'expense' && row.expense) {
          return (  
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='right'
              gap={1}
              height='100%'
              onClick={(e) => e.stopPropagation()}
            >
              <ExpenseActions expense={row.expense} costItemId={row.costItemId} />
            </Stack>
          );
        }

        if (row.type === 'quote' && row.quote) {
          return (
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='right'
              gap={1}
              height='100%'
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title={formatMessage({ id: 'Add expense' })}>
                <IconButton
                  size='small'
                  onClick={() => {
                    setEntryDialog({
                      open: true,
                      costItemId: row.costItemId,
                      type: 'expense',
                      quote: row.quote,
                    });
                  }}
                >
                  <Receipt fontSize='small' />
                </IconButton>
              </Tooltip>
              <QuoteActions quote={row.quote} costItemId={row.costItemId} size='small' />
            </Stack>
          );
        }

        if (row.type === 'cost' && !isTemplate) {
          return (
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='right'
            gap={1}
            height='100%'
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title={formatMessage({ id: 'Add expense' })}>
              <IconButton
                size='medium'
                onClick={() => {
                  if (!row.costItemId) return;
                  setEntryDialog({
                    open: true,
                    costItemId: row.costItemId,
                    type: 'expense',
                  });
                }}
              >
                <Receipt fontSize='small' />
              </IconButton>
            </Tooltip>
            {row.costItemId && <CostItemActions costItemId={row.costItemId} hasEntries={row.hasEntries} />}
          </Stack>
          );
        }

        return null;
      },
    });
  }

  return columns;
};